import {type Palette} from '@bsky.app/alf'

/** Matches OpenTwitter `Accent` / `--accent-*` options. */
export type AccentId =
  | 'blue'
  | 'yellow'
  | 'pink'
  | 'purple'
  | 'orange'
  | 'green'

export const ACCENT_IDS: readonly AccentId[] = [
  'blue',
  'yellow',
  'pink',
  'purple',
  'orange',
  'green',
] as const

/**
 * Core primary ramp overrides per accent (OpenTwitter globals.scss RGB → hex).
 * Remaining primary_* keys stay from the base palette.
 */
const ACCENT_CORE: Record<
  AccentId,
  Pick<
    Palette,
    | 'primary_25'
    | 'primary_50'
    | 'primary_100'
    | 'primary_200'
    | 'primary_300'
    | 'primary_400'
    | 'primary_500'
    | 'primary_600'
    | 'primary_700'
    | 'primary_800'
    | 'primary_900'
    | 'primary_950'
    | 'primary_975'
  >
> = {
  blue: {
    primary_25: '#061f2a',
    primary_50: '#0a2840',
    primary_100: '#0d3a52',
    primary_200: '#0f4a6b',
    primary_300: '#1a8cd8',
    primary_400: '#47b5f5',
    primary_500: '#1d9bf0',
    primary_600: '#1a8cd8',
    primary_700: '#1781c1',
    primary_800: '#146ba3',
    primary_900: '#1e4f6b',
    primary_950: '#0f3a52',
    primary_975: '#0a2a3d',
  },
  yellow: {
    primary_25: '#2a2406',
    primary_50: '#3d3308',
    primary_100: '#52440a',
    primary_200: '#6b5a0c',
    primary_300: '#e6c200',
    primary_400: '#ffd524',
    primary_500: '#ffd500',
    primary_600: '#e6ba00',
    primary_700: '#c9a000',
    primary_800: '#a68400',
    primary_900: '#7a6200',
    primary_950: '#524100',
    primary_975: '#3d3000',
  },
  pink: {
    primary_25: '#2a0614',
    primary_50: '#400a1f',
    primary_100: '#520d28',
    primary_200: '#6b1035',
    primary_300: '#f55fa3',
    primary_400: '#f94896',
    primary_500: '#f91a82',
    primary_600: '#e01574',
    primary_700: '#c11265',
    primary_800: '#9f0f54',
    primary_900: '#7a0c42',
    primary_950: '#520a2d',
    primary_975: '#3d0721',
  },
  purple: {
    primary_25: '#120f2a',
    primary_50: '#1a1540',
    primary_100: '#221d52',
    primary_200: '#2f286b',
    primary_300: '#9d8cff',
    primary_400: '#8b75ff',
    primary_500: '#7857ff',
    primary_600: '#6a4de6',
    primary_700: '#5c42cc',
    primary_800: '#4d38b3',
    primary_900: '#3e2d99',
    primary_950: '#2a1f66',
    primary_975: '#1f174d',
  },
  orange: {
    primary_25: '#2a1406',
    primary_50: '#401d08',
    primary_100: '#52260a',
    primary_200: '#6b330c',
    primary_300: '#ffa040',
    primary_400: '#ff8c1a',
    primary_500: '#ff7a00',
    primary_600: '#e66e00',
    primary_700: '#cc6200',
    primary_800: '#b35600',
    primary_900: '#994a00',
    primary_950: '#663200',
    primary_975: '#4d2600',
  },
  green: {
    primary_25: '#062a1f',
    primary_50: '#08402f',
    primary_100: '#0a523c',
    primary_200: '#0c6b4f',
    primary_300: '#33d9a8',
    primary_400: '#1dcca0',
    primary_500: '#00b87a',
    primary_600: '#00a66e',
    primary_700: '#009462',
    primary_800: '#008256',
    primary_900: '#006f4a',
    primary_950: '#004a32',
    primary_975: '#003a28',
  },
}

export function mergeAccentIntoPalette(
  base: Palette,
  accent: AccentId,
): Palette {
  return {
    ...base,
    ...ACCENT_CORE[accent],
  }
}

/** Swatch for settings UI (primary_500). */
export const ACCENT_SWATCH: Record<AccentId, string> = {
  blue: ACCENT_CORE.blue.primary_500,
  yellow: ACCENT_CORE.yellow.primary_500,
  pink: ACCENT_CORE.pink.primary_500,
  purple: ACCENT_CORE.purple.primary_500,
  orange: ACCENT_CORE.orange.primary_500,
  green: ACCENT_CORE.green.primary_500,
}
